/**
 * useAnimatedNumber.js
 * Custom hook: smooth number interpolation via requestAnimationFrame.
 * Used for live preview rate + payment animations.
 */
import { useState, useEffect, useRef } from 'react';

export function useAnimatedNumber(target, duration = 400) {
  const [display, setDisplay] = useState(target);
  const raf = useRef(null);
  const prevTarget = useRef(target);

  useEffect(() => {
    const from = prevTarget.current;
    prevTarget.current = target;

    if (from === target) return;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (target - from) * eased);
      if (progress < 1) {
        raf.current = requestAnimationFrame(animate);
      }
    };

    raf.current = requestAnimationFrame(animate);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [target, duration]);

  return display;
}
